import React, { useState, useEffect } from "react";
import { Table, Modal, Spinner, Form } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { baseUrl } from "../../../../services/config";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const EMPTY_FORM = {
  name: "",
  in_time: "08:00",
  out_time: "14:00",
  grace_minutes: 15,
  late_count_for_casual_leave: 3,
  missing_days_for_absent: 1,
  attach_sandwich_rule: false,
  monthly_allowed_leaves: 2,
  yearly_allowed_leaves: 24,
  weekly_off_days: [],
};

const AttendanceRules = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/attendance-rules`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.metadata?.success) {
        setRules(data.payload);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const openAdd = () => {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      in_time: rule.in_time,
      out_time: rule.out_time,
      grace_minutes: rule.grace_minutes,
      late_count_for_casual_leave: rule.late_count_for_casual_leave,
      missing_days_for_absent: rule.missing_days_for_absent,
      attach_sandwich_rule: rule.attach_sandwich_rule,
      monthly_allowed_leaves: rule.monthly_allowed_leaves,
      yearly_allowed_leaves: rule.yearly_allowed_leaves,
      weekly_off_days: rule.weekly_off_days || [],
    });
    setErrors({});
    setShowModal(true);
  };

  const handleField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleOffDay = (day) => {
    setForm((f) => {
      const days = f.weekly_off_days.includes(day)
        ? f.weekly_off_days.filter((d) => d !== day)
        : [...f.weekly_off_days, day];
      return { ...f, weekly_off_days: days };
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    if (!form.in_time) errs.in_time = "In time is required.";
    if (!form.out_time) errs.out_time = "Out time is required.";
    if (form.out_time && form.in_time && form.out_time <= form.in_time)
      errs.out_time = "Out time must be after in time.";
    if (form.grace_minutes === "" || form.grace_minutes < 0 || form.grace_minutes > 1440)
      errs.grace_minutes = "Grace minutes must be 0–1440.";
    if (form.late_count_for_casual_leave === "" || form.late_count_for_casual_leave < 1)
      errs.late_count_for_casual_leave = "Minimum 1.";
    if (form.missing_days_for_absent === "" || form.missing_days_for_absent < 1)
      errs.missing_days_for_absent = "Minimum 1.";
    if (form.monthly_allowed_leaves === "" || form.monthly_allowed_leaves < 0)
      errs.monthly_allowed_leaves = "Minimum 0.";
    if (form.yearly_allowed_leaves === "" || form.yearly_allowed_leaves < 0)
      errs.yearly_allowed_leaves = "Minimum 0.";
    if (!form.weekly_off_days.length) errs.weekly_off_days = "Select at least one off day.";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    const url = editingRule
      ? `${baseUrl}/attendance-rules/${editingRule.id}`
      : `${baseUrl}/attendance-rules`;
    const method = editingRule ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authtoken}` },
        body: JSON.stringify({
          ...form,
          grace_minutes: Number(form.grace_minutes),
          late_count_for_casual_leave: Number(form.late_count_for_casual_leave),
          missing_days_for_absent: Number(form.missing_days_for_absent),
          monthly_allowed_leaves: Number(form.monthly_allowed_leaves),
          yearly_allowed_leaves: Number(form.yearly_allowed_leaves),
        }),
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show(editingRule ? "Rule updated successfully." : "Rule created successfully.");
        setShowModal(false);
        fetchRules();
      } else {
        const msg = data.metadata?.message;
        if (typeof msg === "object") setErrors(msg);
        else alert.error(msg || "Failed to save rule.");
      }
    } catch (err) {
      console.log(err);
      alert.error("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (rule) => {
    setDeleteTarget(rule);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${baseUrl}/attendance-rules/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await res.json();
      if (data.metadata?.success) {
        alert.show("Rule deleted successfully.");
        setShowDeleteModal(false);
        fetchRules();
      } else {
        alert.error(data.metadata?.message || "Failed to delete rule.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 style={{ margin: 0 }}>Attendance Rules</h4>
        <button onClick={openAdd} className="btn btn-success button_blue">
          + Add Rule
        </button>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Table responsive bordered hover>
          <thead className="tablehead">
            <tr className="tableheadtr text-center">
              <th className="background-color-primary tableHEadNAme">#</th>
              <th className="background-color-primary tableHEadNAme">Name</th>
              <th className="background-color-primary tableHEadNAme">In Time</th>
              <th className="background-color-primary tableHEadNAme">Out Time</th>
              <th className="background-color-primary tableHEadNAme">Grace (min)</th>
              <th className="background-color-primary tableHEadNAme">Late → Leave</th>
              <th className="background-color-primary tableHEadNAme">Missing → Absent</th>
              <th className="background-color-primary tableHEadNAme">Sandwich Rule</th>
              <th className="background-color-primary tableHEadNAme">Monthly Leaves</th>
              <th className="background-color-primary tableHEadNAme">Yearly Leaves</th>
              <th className="background-color-primary tableHEadNAme">Weekly Off Days</th>
              <th className="background-color-primary tableHEadNAme">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center text-muted py-4">
                  No rules found. Click "+ Add Rule" to create one.
                </td>
              </tr>
            ) : (
              rules.map((rule, i) => (
                <tr key={rule.id}>
                  <td>{i + 1}</td>
                  <td><strong>{rule.name}</strong></td>
                  <td>{rule.in_time}</td>
                  <td>{rule.out_time}</td>
                  <td>{rule.grace_minutes}</td>
                  <td>{rule.late_count_for_casual_leave}</td>
                  <td>{rule.missing_days_for_absent}</td>
                  <td>{rule.attach_sandwich_rule ? "Yes" : "No"}</td>
                  <td>{rule.monthly_allowed_leaves}</td>
                  <td>{rule.yearly_allowed_leaves}</td>
                  <td>
                    {(rule.weekly_off_days || []).map((d) => (
                      <span
                        key={d}
                        style={{
                          background: "var(--primary-btn-color)",
                          color: "#fff",
                          borderRadius: 4,
                          padding: "2px 7px",
                          fontSize: 11,
                          marginRight: 4,
                          display: "inline-block",
                          marginBottom: 2,
                          textTransform: "capitalize",
                        }}
                      >
                        {d}
                      </span>
                    ))}
                  </td>
                  <td className="text-center">
                    <span
                      onClick={() => openEdit(rule)}
                      style={{ cursor: "pointer", marginRight: 12 }}
                      className="svg-icon svg-icon-md svg-icon-primary-custom"
                      title="Edit"
                    >
                      <SVG src={toAbsoluteUrl("/media/svg/icons/General/Edit.svg")} />
                    </span>
                    <span
                      onClick={() => confirmDelete(rule)}
                      style={{ cursor: "pointer" }}
                      className="svg-icon svg-icon-md svg-icon-danger"
                      title="Delete"
                    >
                      <SVG src={toAbsoluteUrl("/media/svg/icons/General/Trash.svg")} />
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {editingRule ? "Edit Attendance Rule" : "Add Attendance Rule"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-control-label"><b>Rule Name <span className="text-danger">*</span></b></label>
              <input
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                value={form.name}
                onChange={(e) => handleField("name", e.target.value)}
                placeholder="e.g. Default Staff Rule"
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-control-label"><b>In Time <span className="text-danger">*</span></b></label>
              <input
                type="time"
                className={`form-control ${errors.in_time ? "is-invalid" : ""}`}
                value={form.in_time}
                onChange={(e) => handleField("in_time", e.target.value)}
              />
              {errors.in_time && <div className="invalid-feedback">{errors.in_time}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-control-label"><b>Out Time <span className="text-danger">*</span></b></label>
              <input
                type="time"
                className={`form-control ${errors.out_time ? "is-invalid" : ""}`}
                value={form.out_time}
                onChange={(e) => handleField("out_time", e.target.value)}
              />
              {errors.out_time && <div className="invalid-feedback">{errors.out_time}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-control-label"><b>Grace Minutes <span className="text-danger">*</span></b></label>
              <input
                type="number"
                min={0}
                max={1440}
                className={`form-control ${errors.grace_minutes ? "is-invalid" : ""}`}
                value={form.grace_minutes}
                onChange={(e) => handleField("grace_minutes", e.target.value)}
              />
              {errors.grace_minutes && <div className="invalid-feedback">{errors.grace_minutes}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-control-label"><b>Late Days → Casual Leave <span className="text-danger">*</span></b></label>
              <input
                type="number"
                min={1}
                className={`form-control ${errors.late_count_for_casual_leave ? "is-invalid" : ""}`}
                value={form.late_count_for_casual_leave}
                onChange={(e) => handleField("late_count_for_casual_leave", e.target.value)}
              />
              {errors.late_count_for_casual_leave && <div className="invalid-feedback">{errors.late_count_for_casual_leave}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-control-label"><b>Missing Days → Absent <span className="text-danger">*</span></b></label>
              <input
                type="number"
                min={1}
                className={`form-control ${errors.missing_days_for_absent ? "is-invalid" : ""}`}
                value={form.missing_days_for_absent}
                onChange={(e) => handleField("missing_days_for_absent", e.target.value)}
              />
              {errors.missing_days_for_absent && <div className="invalid-feedback">{errors.missing_days_for_absent}</div>}
            </div>

            <div className="col-md-4 mb-3 d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label={<b>Attach Sandwich Rule</b>}
                checked={form.attach_sandwich_rule}
                onChange={(e) => handleField("attach_sandwich_rule", e.target.checked)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-control-label"><b>Monthly Allowed Leaves <span className="text-danger">*</span></b></label>
              <input
                type="number"
                min={0}
                className={`form-control ${errors.monthly_allowed_leaves ? "is-invalid" : ""}`}
                value={form.monthly_allowed_leaves}
                onChange={(e) => handleField("monthly_allowed_leaves", e.target.value)}
              />
              {errors.monthly_allowed_leaves && <div className="invalid-feedback">{errors.monthly_allowed_leaves}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-control-label"><b>Yearly Allowed Leaves <span className="text-danger">*</span></b></label>
              <input
                type="number"
                min={0}
                className={`form-control ${errors.yearly_allowed_leaves ? "is-invalid" : ""}`}
                value={form.yearly_allowed_leaves}
                onChange={(e) => handleField("yearly_allowed_leaves", e.target.value)}
              />
              {errors.yearly_allowed_leaves && <div className="invalid-feedback">{errors.yearly_allowed_leaves}</div>}
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-control-label"><b>Weekly Off Days <span className="text-danger">*</span></b></label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
                {WEEKDAYS.map((day) => (
                  <label
                    key={day}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 12px",
                      border: `2px solid ${form.weekly_off_days.includes(day) ? "var(--primary-btn-color)" : "#ccc"}`,
                      borderRadius: 20,
                      cursor: "pointer",
                      backgroundColor: form.weekly_off_days.includes(day) ? "var(--primary-btn-color)" : "#fff",
                      color: form.weekly_off_days.includes(day) ? "#fff" : "#333",
                      textTransform: "capitalize",
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.weekly_off_days.includes(day)}
                      onChange={() => toggleOffDay(day)}
                      style={{ display: "none" }}
                    />
                    {day}
                  </label>
                ))}
              </div>
              {errors.weekly_off_days && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                  {errors.weekly_off_days}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handleSubmit} disabled={saving} className="btn btn-success button_blue">
            {saving ? <Spinner size="sm" animation="border" /> : (editingRule ? "Update" : "Save")}
          </button>
          <button onClick={() => setShowModal(false)} className="btn btn-danger btn_width">
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">Delete Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">
            {deleting ? <Spinner size="sm" animation="border" /> : "Delete"}
          </button>
          <button onClick={() => setShowDeleteModal(false)} className="btn btn-secondary">
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
};

export default AttendanceRules;
